import {
  type ReactNode,
  useCallback,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react'

import {useBoardFocusStore} from '../focus/contexts'
import {useBoardInteraction} from '../provider/contexts'
import {boardSelectionKey} from '../selection/next-selection'
import type {BoardCardAddress} from '../types'
import {type BoardDialogReport, BoardDialogReportContext} from './contexts'
import type {BoardReporting} from './types'

const NO_CARDS: readonly BoardCardAddress[] = []

/**
 * Tells the caller what somebody is touching: which card has focus, which have a dialog
 * open, and which are being dragged.
 *
 * Rendered inside the focus provider, because the first of those is read from the focus
 * store. It holds no state of its own, so it re-renders only when the provider around it
 * does, and neither of the components under it draws anything.
 */
export function BoardReportingProvider({
  onFocusedCardChange,
  onCardDialogChange,
  onDragChange,
  children,
}: BoardReporting & {children: ReactNode}): ReactNode {
  // The cards a caller's own components have said they opened something on. A ref rather
  // than state, because nothing on the board draws differently for it: the whole of what
  // it is for is the call below, and holding it in state would re-render every card on the
  // board each time somebody opened a menu.
  const openCardsRef = useRef<readonly BoardCardAddress[]>(NO_CARDS)

  /*
    The caller's own callback, held rather than closed over, so the context below is one
    object for the life of the board.

    `useEffectEvent` reads the current one too and would be the obvious choice, but it hands
    back a new function on every render, so anything memoised on it is memoised on nothing.
    A context value rebuilt per render re-renders every card that reads it, on every render
    of the page around the board, which is the exact cost the board's contexts are split by
    cadence to avoid.
  */
  const onCardDialogChangeRef = useRef(onCardDialogChange)

  // A layout effect, because a card taken off the board reports its dialog closed from its
  // own passive cleanup. Passive effects run parents before children, but cleanups run
  // children before parents, so a caller that swapped this callback in the same render that
  // removed the card would otherwise hear about the close through the callback it had just
  // replaced.
  useLayoutEffect(() => {
    onCardDialogChangeRef.current = onCardDialogChange
  }, [onCardDialogChange])

  const reportDialogOpen = useCallback((card: BoardCardAddress, isOpen: boolean) => {
    const key = boardSelectionKey(card)
    const open = openCardsRef.current
    const wasOpen = open.some(other => boardSelectionKey(other) === key)
    // Reported only when it really changed, the same rule `onSelectionChange` follows: a
    // card closing a menu it never said it opened is not news.
    if (wasOpen === isOpen) return

    openCardsRef.current = isOpen ? [...open, card] : open.filter(other => boardSelectionKey(other) !== key)
    onCardDialogChangeRef.current?.(openCardsRef.current)
  }, [])

  const dialogReport = useMemo<BoardDialogReport>(() => ({reportDialogOpen}), [reportDialogOpen])

  return (
    <BoardDialogReportContext value={dialogReport}>
      <BoardFocusReport onFocusedCardChange={onFocusedCardChange} />
      <BoardDragReport onDragChange={onDragChange} />
      {children}
    </BoardDialogReportContext>
  )
}

/**
 * Reports which card focus is on.
 *
 * **Renders nothing, deliberately.** It subscribes to a value that changes on every move,
 * which is the one subscription the focus store's whole shape exists to keep away from the
 * cards: answering "where is focus" through anything a card reads would redraw every card
 * on the board for one arrow key. A null-rendering sibling pays for it once.
 *
 * The report is the card, or nothing. A cell, a row header and each paging ghost are all
 * positions and none of them is a card, and neither is a seeded position nobody has taken
 * focus at, so all of them report `null`.
 */
function BoardFocusReport({onFocusedCardChange}: Pick<BoardReporting, 'onFocusedCardChange'>): ReactNode {
  const {getState, subscribe} = useBoardFocusStore()

  // The state object itself, which the reducer returns untouched whenever a move settled
  // where it started, so this is stable between real changes.
  const getSnapshot = useCallback(() => getState(), [getState])
  const {position, hasFocus} = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  const card: BoardCardAddress | null =
    hasFocus && position !== null && position.kind === 'cell' && position.itemId !== null
      ? {columnKey: position.columnKey, rowKey: position.rowKey, itemId: position.itemId}
      : null

  const report = useEffectEvent(() => {
    onFocusedCardChange?.(card)
  })

  // The three keys rather than the card, so the effect runs when the answer changed and
  // not when the position object was rebuilt around the same one.
  const columnKey = card?.columnKey
  const rowKey = card?.rowKey
  const itemId = card?.itemId

  // A board nobody has touched reports nothing, so the first run is a baseline rather than
  // a report. Only when it really is the empty one: focus can already be on a card by the
  // time this runs, because a card's own focus effect is a layout effect and runs first,
  // and a reader who tabbed straight in must not be silently dropped.
  const hasReportedRef = useRef(false)

  useEffect(() => {
    if (!hasReportedRef.current) {
      hasReportedRef.current = true
      // `itemId` rather than the card, because it is the same question asked of a value
      // the effect already depends on: it is set exactly when a card has focus.
      if (itemId === undefined) return
    }

    /*
      An effect calling a prop rather than an event handler doing it, and deliberately: the
      thing that happened is the focus store settling somewhere new, which is a
      subscription rather than an event. There is no handler to move this into, because the
      same move arrives from a key, from a click, from `focusPosition` and from new data
      repairing the position.
    */

    report()
  }, [columnKey, rowKey, itemId])

  return null
}

/**
 * Reports which cards are being dragged.
 *
 * Reads the interaction context `drag/BoardDragProvider.tsx` publishes, which is the same
 * one the auto-scroll reads, so a drag is one fact rather than two.
 *
 * Renders nothing, for the same reason the focus report does: a drag changes on pointer
 * movement.
 */
function BoardDragReport({onDragChange}: Pick<BoardReporting, 'onDragChange'>): ReactNode {
  const {draggingCards} = useBoardInteraction()

  const report = useEffectEvent(() => {
    onDragChange?.(draggingCards)
  })

  // A board nobody has touched reports nothing, so the first run is a baseline rather than
  // a report. The condition is kept rather than assumed: this report and the context it
  // reads mount together today, so an empty first run is the only one there is, but a
  // publisher that ever moved above the board would make a drag in flight reportable.
  const hasReportedRef = useRef(false)

  useEffect(() => {
    if (!hasReportedRef.current) {
      hasReportedRef.current = true
      if (draggingCards.length === 0) return
    }

    report()
  }, [draggingCards])

  return null
}
